/**
 * PostHog browser SDK initialization with PHI-safe defaults. Port of legacy
 * `src/components/providers/posthog-provider.tsx` `initPostHog()` /
 * `teardownPostHog()` — separated so consumers can drive the lifecycle from
 * any framework (Next.js App Router, TanStack Router, Vite SPA, etc.).
 *
 * Idempotent: window-scoped flag prevents double-init on consent re-grants
 * after revoke. After teardown the flag stays set so a subsequent `init()`
 * skips the heavy `posthog.init()` and just calls `opt_in_capturing()`.
 */

import posthog, { type CaptureResult } from "posthog-js";

import { redactUrlSecrets, redactUrlSecretsIn } from "../redact-url";

declare global {
  interface Window {
    __POSTHOG_INITIALIZED__?: boolean;
  }
}

export interface PostHogConfig {
  /** PostHog project API key (`NEXT_PUBLIC_POSTHOG_KEY`). When missing,
   *  init becomes a no-op so apps can mount the provider unconditionally. */
  apiKey: string | undefined;
  /** Ingest host. Defaults to the same-origin `/relay-pm-ev` reverse-proxy
   *  path so ad blockers can't drop requests by domain match. The proxy
   *  forwards to `https://eu.i.posthog.com` (EU residency for GDPR/PHI).
   *  Pages Functions in apps/app + apps/admin and Next.js rewrites in
   *  apps/web implement the proxy — keep them in sync. */
  apiHost?: string;
  /** Set to true to enable verbose `posthog.debug()` output. */
  debug?: boolean;
  /** Fraction of sessions to record (0–1). Defaults to **0.2 (20%)** to
   *  cap recording costs (PostHog charges per recording on paid plans —
   *  100% sampling at 5k DAU = ~$300/month vs. ~$60 at 20%). Set to 1 in
   *  apps/admin where every staff session is worth seeing for audit. The
   *  decision is made once per page load (not per event) so a sampled
   *  session is consistent end-to-end. */
  recordingSampleRate?: number;
  /** Disable PostHog autocapture entirely. Useful for internal apps
   *  (apps/admin) where staff don't need auto click tracking — only
   *  explicit captures. Reduces noise + bandwidth on those sessions. */
  disableAutocapture?: boolean;
}

/**
 * Default ingest path. Same-origin so requests look like `pontimed.app/relay-pm-ev/decide`
 * instead of `eu.i.posthog.com/decide` — bypasses adblock filter lists.
 */
const DEFAULT_API_HOST = "/relay-pm-ev";

/**
 * PostHog UI host (dashboard, recordings, toolbar deep links). Stays
 * direct because it's user-facing dashboards on PostHog's domain — not
 * SDK ingest traffic. The toolbar embeds these links so admins clicking
 * "view in PostHog" land on the right project.
 */
const POSTHOG_UI_HOST = "https://eu.posthog.com";

/** An event with the credentials in its URLs redacted (../redact-url). */
function redactEventUrls(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event;
  redactUrlSecretsIn(event.properties);
  redactUrlSecretsIn(event.$set);
  redactUrlSecretsIn(event.$set_once);
  return event;
}

export function initPostHog(config: PostHogConfig): void {
  if (typeof window === "undefined" || !config.apiKey) return;
  if (window.__POSTHOG_INITIALIZED__) {
    posthog.opt_in_capturing();
    return;
  }

  // Empty string from a missing env var must NOT pin api_host to "" —
  // treat empty/whitespace as absent so the relay-proxy default applies.
  const resolvedApiHost = config.apiHost?.trim() ? config.apiHost : DEFAULT_API_HOST;

  // Session-recording sampling decided once per page load. Rolled at init
  // time so a sampled session captures the full flow start-to-finish.
  // Default 20% — see config.recordingSampleRate doc for cost rationale.
  const recordingSampleRate = config.recordingSampleRate ?? 0.2;
  const shouldRecord = Math.random() < recordingSampleRate;

  posthog.init(config.apiKey, {
    api_host: resolvedApiHost,
    ui_host: POSTHOG_UI_HOST,
    // Pin to 2026-01-30 defaults — bundles autocapture/pageview/pageleave/
    // session-recording with sensible modern settings. PHI overrides below.
    defaults: "2026-01-30",
    // Don't create person profiles for anonymous visitors — only logged-in
    // users via the identity bridge.
    person_profiles: "identified_only",

    // Web Analytics setup checks. PostHog's dashboard surfaces these as
    // explicit toggle flags (it inspects config object, not defaults) so
    // we set them here to make the "Web analytics setup" panel green.
    capture_pageview: true,
    // `$pageleave` powers bounce rate + session duration + scroll depth.
    // Modern posthog-js fires it on `pagehide` so we capture the time
    // between mount + actual unload (more accurate than relying on
    // visibilitychange alone).
    capture_pageleave: true,
    // Core Web Vitals (LCP, INP, CLS) → PostHog "Web vitals" dashboard.
    // Lightweight: <1KB extra + only fires once per page on `pagehide`.
    capture_performance: {
      web_vitals: true,
      network_timing: true,
    },

    // Skip recording entirely on the 80% of sessions outside the sample.
    // PostHog still respects this even after init; programmatic
    // start/stop via `posthog.startSessionRecording()` is available if a
    // surface needs to opt back in mid-session (admin debug etc.).
    disable_session_recording: !shouldRecord,

    // Don't capture anything until consent is checked + opt-in is called below.
    // Belt-and-suspenders: even though the provider only mounts on consent,
    // posthog-js boots in opted-out mode so any race against the consent
    // cookie can never leak a single event.
    opt_out_capturing_by_default: true,

    // ── PHI-safety overrides ──
    // Session recordings: mask all inputs by default + block selectors for
    // medical content. Inputs include patient symptoms, diagnoses, medication
    // names, dates of birth, ID numbers — all PHI under GDPR/NIS2.
    session_recording: {
      maskAllInputs: true,
      // Fully block elements explicitly marked as PHI (replaced with
      // placeholder rect in replay timeline — invisible to anyone watching
      // the recording).
      blockSelector: ".phi-block, [data-phi-block], [data-medical], [data-ph-no-capture]",
      // Mask text content of these selectors (rendered as ▒ in replays).
      maskTextSelector: ".phi-mask, [data-phi-mask]",
      // Strip request/response bodies from network captures — bodies often
      // contain PHI (medical records, prescriptions, lab results).
      maskCapturedNetworkRequestFn: (request) => ({
        ...request,
        // A one-tap security link's `token` never leaves the browser (../redact-url).
        name: redactUrlSecrets(request.name),
        requestBody: undefined,
        responseBody: undefined,
      }),
    },

    // Don't capture form input values via autocapture (PHI risk). Internal
    // apps (apps/admin) opt out entirely to keep dashboards focused on
    // patient/provider flows.
    autocapture: config.disableAutocapture
      ? false
      : {
          element_attribute_ignorelist: [
            "value",
            "placeholder",
            "data-phi",
            "data-medical",
            "data-ph-no-capture",
          ],
        },

    // A one-tap security link's `token` (../redact-url) never reaches an
    // event: $current_url, $referrer and the person's initial URL included.
    before_send: redactEventUrls,

    // GDPR-friendly: don't fingerprint anonymous users by IP.
    ip: false,
    property_denylist: ["$ip", "$initial_referring_domain", "$referring_domain"],

    loaded: (ph) => {
      if (config.debug) ph.debug();
    },
  });

  window.__POSTHOG_INITIALIZED__ = true;
  posthog.opt_in_capturing();
}

/**
 * Stop capturing + clear the cached `$distinct_id` so the next opt-in starts
 * fresh and we don't leak the old identifier across consent boundaries.
 */
export function teardownPostHog(): void {
  if (typeof window === "undefined" || !window.__POSTHOG_INITIALIZED__) return;
  posthog.opt_out_capturing();
  posthog.reset();
}
